import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAtomValue } from 'jotai';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import ShareIcon from '@mui/icons-material/Share';
import { Icon as MuiIcon, Typography, useTheme } from '@mui/material';
import MuiTouchRipple, { TouchRippleActions, TouchRippleProps } from '@mui/material/ButtonBase/TouchRipple';
import { styled } from '@mui/material/styles';
import {
  FC,
  ReactElement,
  RefAttributes,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

// MUI types TouchRipple as a bare ForwardRefRenderFunction, which React 19 rejects as a JSX
// element type; the runtime export is a real forwardRef component.
const TouchRipple = MuiTouchRipple as unknown as FC<TouchRippleProps & RefAttributes<TouchRippleActions>>;

import { VIEW_TYPE } from '../../../../../data-models/view-config.data-model';
import { ShareViewModal } from '../../../../../pages/PortfolioOverview/components/ShareViewModal/ShareViewModal';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { useRenameView } from '../../../../../pages/PortfolioOverview/hooks/useViewActions';
import { usePromoteViewOption } from '../../../../../pages/PortfolioOverview/promote-view/PromoteView';
import { selectedViewPFJ } from '../../../../../pages/PortfolioOverview/state/ViewStateJ';
import { ViewAction, ViewActionName } from '../../../../../types';
import { BasicDialog } from '../../../../Dialog/BasicDialog';
import { EditableTypography } from '../../../../EditableTypography/EditableTypography';
import { GenericFallbacksWrapper } from '../../../../Fallback/GenericFallbacksWrapper';
import { Icon } from '../../../../Icon';
import { ViewMenu } from './ViewMenu';

interface Props {
  text: string;
  onClick: () => void;
  isSelected?: boolean;
  isDraggable: boolean;
  viewTypeIcon?: string;
  viewType: VIEW_TYPE;
  suppressMenu?: boolean;
}

const MENU_CLASS = 'view-option-menu';

const IconWrapper = styled('div')`
  min-width: 35px;
  margin-bottom: -3px;
  visibility: hidden;
`;
const LabelWrapper = styled('div')<{ isShared: boolean }>`
  ${({ isShared }) => (isShared ? `padding-left: 5px;` : '')}
`;

const ViewTypeIconWrapper = styled('div')`
  margin-bottom: -3px;
  svg {
    width: 15px;
    height: 15px;
    stroke: white;
  }
`;

const StyledOption = styled('li')<{ isSelected: boolean }>`
  height: 32px;
  padding-left: 3px;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  ${({ isSelected }) => (isSelected ? 'background-color: #2D3277;' : '')}
  cursor: pointer;

  &:hover .${MENU_CLASS} {
    visibility: visible;
  }
`;

const OptionContentWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.1rem;
`;

const EditableTypographyWrapper = styled('div')``;

export const ViewOption = ({
  text,
  onClick,
  isSelected = false,
  isDraggable,
  viewTypeIcon,
  viewType,
  suppressMenu,
}: Props): ReactElement => {
  const [editable, setEditable] = useState(false);
  const [shareModelIsOpen, setShareModelIsOpen] = useState(false);
  const { colors } = useTheme();
  const renameView = useRenameView();
  const selectedView = useAtomValue(selectedViewPFJ);
  const rippleRef = useRef<null | TouchRippleActions>(null);
  const isAuditor = PermissionService.get().isAuditor();

  const onRippleStart = (e: SyntheticEvent) => {
    rippleRef.current?.start(e);
  };
  const onRippleStop = (e: SyntheticEvent) => {
    rippleRef.current?.stop(e);
  };

  const onModalClose = useCallback(() => {
    setShareModelIsOpen(false);
  }, []);

  const handleDoubleClick = () => {
    if (isSelected && !isAuditor) {
      setEditable(true);
    }
  };

  const onSubmit = async (newName: string) => {
    if (!selectedView || newName === selectedView.label) {
      setEditable(false);
      return;
    }

    await renameView(selectedView, newName);
    setEditable(false);
  };

  const promoteViewOption = usePromoteViewOption();

  // PermissionService is a session-lifetime singleton initialized once after login,
  // so its boolean checks inside buildViewMenuOptions are stable across renders and
  // don't need to appear in the dependency array.
  const options = useMemo(
    () => buildViewMenuOptions(viewType, promoteViewOption),
    [promoteViewOption, viewType]
  );

  return (
    <StyledOption
      onMouseDown={onRippleStart}
      onMouseUp={onRippleStop}
      role='option'
      onClick={onClick}
      isSelected={isSelected}
    >
      <TouchRipple ref={rippleRef} center={false} />
      <OptionContentWrapper>
        <>
          {isDraggable ? (
            <DragIndicatorIcon sx={{ fill: colors.primary[30] }} fontSize='small' />
          ) : (
            <MuiIcon fontSize='small' />
          )}
        </>
        <ViewTypeIconWrapper>
          {viewTypeIcon && <Icon type={viewTypeIcon} color={colors.secondary[20]} />}
        </ViewTypeIconWrapper>
        {viewType === VIEW_TYPE.PERSONAL ? (
          <EditableTypographyWrapper onDoubleClick={handleDoubleClick}>
            <EditableTypography
              variant='caption'
              color={colors.secondary[10]}
              content={text}
              editable={editable}
              handleSubmit={onSubmit}
              inputColor={colors.secondary[10]}
              width={150}
            />
          </EditableTypographyWrapper>
        ) : (
          <LabelWrapper isShared={viewType === VIEW_TYPE.SHARED}>
            <Typography variant='caption' color={colors.secondary[10]}>
              {text}
            </Typography>
          </LabelWrapper>
        )}
      </OptionContentWrapper>
      {suppressMenu || isAuditor
        ? null
        : isSelected && (
            <IconWrapper className={MENU_CLASS}>
              <ViewMenu
                onRename={() => setEditable(true)}
                openShareDialog={() => setShareModelIsOpen(true)}
                options={options}
              />
            </IconWrapper>
          )}
      <BasicDialog
        open={shareModelIsOpen}
        onClose={onModalClose}
        title={`Share view "${selectedView?.name}"`}
      >
        {shareModelIsOpen && (
          <GenericFallbacksWrapper>
            <ShareViewModal onCloseClick={onModalClose} />
          </GenericFallbacksWrapper>
        )}
      </BasicDialog>
    </StyledOption>
  );
};

export function buildViewMenuOptions(
  viewType: VIEW_TYPE,
  promoteViewOption: ViewAction | null
): ViewAction[] {
  switch (viewType) {
    case VIEW_TYPE.PERSONAL: {
      const personalOpts: ViewAction[] = [
        {
          id: ViewActionName.RENAME,
          value: 'Rename',
          icon: <EditIcon fontSize='small' />,
        },
        {
          id: ViewActionName.DUPLICATE,
          value: 'Duplicate View',
          icon: <ContentCopyIcon fontSize='small' />,
        },
        {
          id: ViewActionName.SHARE,
          value: 'Share View',
          icon: <ShareIcon fontSize='small' />,
        },
        {
          id: ViewActionName.DELETE,
          value: 'Delete View',
          icon: <DeleteOutlineIcon fontSize='small' />,
          hasSeparator: true,
        },
      ];
      if (promoteViewOption) {
        personalOpts.splice(3, 0, promoteViewOption);
      }
      return personalOpts;
    }
    case VIEW_TYPE.COMPANY: {
      const companyOpts: ViewAction[] = [
        {
          id: ViewActionName.DUPLICATE,
          value: 'Duplicate View',
          icon: <ContentCopyIcon fontSize='small' />,
        },
      ];
      if (PermissionService.get().hasPermission(PermissionKey.canViewClientSettings)) {
        companyOpts.push({
          id: ViewActionName.SET_AS_LANDING,
          value: 'Set as landing view',
          icon: <HomeIcon fontSize='small' />,
        });
      }
      if (PermissionService.get().canManageDefaultViews()) {
        companyOpts.push({
          id: ViewActionName.DELETE,
          value: 'Delete View',
          icon: <DeleteOutlineIcon fontSize='small' />,
          hasSeparator: true,
        });
      }
      return companyOpts;
    }
    case VIEW_TYPE.SHARED:
      return [
        {
          id: ViewActionName.DUPLICATE,
          value: 'Duplicate View',
          icon: <ContentCopyIcon fontSize='small' />,
        },
        {
          id: ViewActionName.DELETE,
          value: 'Delete View',
          icon: <DeleteOutlineIcon fontSize='small' />,
          hasSeparator: true,
        },
      ];
  }
}
